import { useState } from 'react'
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

export type CardVariant = 'plain' | 'tint' | 'framed'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
  /** Inner padding (number → px). Defaults to 20. */
  padding?: number
  /** Lift the card on hover (shadow + translate). */
  hoverable?: boolean
  children?: ReactNode
}

const VARIANTS: Record<CardVariant, CSSProperties> = {
  plain: {
    background: 'var(--surface-card)',
    border: '1px solid var(--border-soft)',
    boxShadow: 'var(--shadow-sm)',
  },
  tint: {
    background: 'var(--surface-tint)',
    border: '1px solid var(--emerald-100)',
    boxShadow: 'var(--shadow-xs)',
  },
  framed: {
    background: 'var(--surface-card)',
    border: '2px solid var(--border-gold)',
    boxShadow: 'var(--shadow-gold)',
  },
}

export function Card({
  variant = 'plain',
  padding = 20,
  hoverable,
  children,
  style,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: CardProps) {
  const [hover, setHover] = useState(false)
  const lift = hoverable && hover
  return (
    <div
      onMouseEnter={(e) => {
        setHover(true)
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        setHover(false)
        onMouseLeave?.(e)
      }}
      style={{
        borderRadius: 'var(--radius-lg)',
        padding,
        transition:
          'box-shadow var(--dur-base) var(--ease-out), transform var(--dur-base) var(--ease-out)',
        ...VARIANTS[variant],
        ...(lift
          ? { boxShadow: 'var(--shadow-md)', transform: 'translateY(-2px)' }
          : null),
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  )
}

export default Card
